import React from "react";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import closeIcon from "@src/assets/svg/close-icon-gray.svg";
import eyeIcon from "@src/assets/svg/eye-icon.svg";
import eyeSlashIcon from "@src/assets/svg/eye-slash-icon.svg";
import styles from "./ota-push-firmware-modal.module.scss";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { PrivateKeyUploadField } from "../../../atoms/private-key-upload-field/private-key-upload-field";
import { OTASecondaryButton } from "@src/components/atoms/buttons/ota-secondary-button/ota-secondary-button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { useOtaPushFirmwareModal } from "./use-ota-push-firmware-modal";
import { ListItem } from "@src/types/ota-update";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";

const cssClassName = "ota-push-firmware-modal";

const OTAPushFirmwareModal = ({
  data,
  onClose,
  setInstallAndActionBlock,
}: {
  data: ListItem;
  onClose: () => void;
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >;
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

  const {
    targetPath,
    privateKeyFile,
    username,
    password,
    privateKeyPassword,
    firmware,
    machine,
    folders,
    texts,
    handleCloseModal,
    handleTargetPathChange,
    handleUsernameChange,
    handlePasswordChange,
    handlePrivateKeyFileSelection,
    handlePrivateKeyPasswordChange,
    handleAskForApproval,
    isAskForApprovalLoading,
  } = useOtaPushFirmwareModal(data, onClose, setInstallAndActionBlock);

  const handleTogglePasswordVisibility = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsPasswordVisible((previousState) => !previousState);
  };

  return (
    <div className={styles[cssClassName]}>
      <div
        className={styles[`${cssClassName}__overlay`]}
        aria-label={texts.closePushFirmwareModalAriaLabel}
      />
      <div className={styles[`${cssClassName}__dialog-wrapper`]}>
        <div
          className={styles[`${cssClassName}__modal-body`]}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles[`${cssClassName}__header`]}>
            <h2 className={styles[`${cssClassName}__title`]}>{texts.title}</h2>
            <IconButton
              fun={handleCloseModal}
              icon={closeIcon}
              tooltipPosition="top"
              variant="close-button"
              disabled={isAskForApprovalLoading}
            />
          </div>

          <div className={styles[`${cssClassName}__form`]}>
            <FlexColumnContainer verticalGap={10} alignItems="stretch">
              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.firmwareSectionTitle}
                </p>

                <div className={styles[`${cssClassName}__info-card`]}>
                  <p className={styles[`${cssClassName}__firmware-name`]}>
                    {firmware.fileName}
                    <span className={styles[`${cssClassName}__firmware-size`]}>
                      ({firmware.fileSize})
                    </span>
                  </p>
                  <p className={styles[`${cssClassName}__meta-text`]}>
                    {texts.uploadedAtLabel} {firmware.uploadedAt}
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.machineSectionTitle}
                </p>

                <div className={styles[`${cssClassName}__machine-card`]}>
                  <p className={styles[`${cssClassName}__machine-name`]}>
                    {machine.name}{" "}
                    <span className={styles[`${cssClassName}__machine-ip`]}>
                      ({machine.ip})
                    </span>
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.foldersSectionTitle}
                </p>

                <div
                  className={`${styles[`${cssClassName}__folders-list-card`]} ${
                    styles[`${cssClassName}__folders-card`]
                  }`}
                >
                  {folders?.map((folder) => (
                    <p
                      key={folder}
                      className={styles[`${cssClassName}__folder-name`]}
                    >
                      \{folder}
                    </p>
                  ))}
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <label
                  className={styles[`${cssClassName}__field-label`]}
                  htmlFor="push-firmware-target-path"
                >
                  {texts.targetPathLabel}
                </label>
                <input
                  id="push-firmware-target-path"
                  type="text"
                  value={targetPath}
                  onChange={handleTargetPathChange}
                  placeholder={texts.targetPathPlaceholder}
                  className={styles[`${cssClassName}__text-input`]}
                />
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <label
                  className={styles[`${cssClassName}__field-label`]}
                  htmlFor="push-firmware-username"
                >
                  {texts.usernameLabel}
                </label>
                <input
                  id="push-firmware-username"
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder={texts.usernamePlaceholder}
                  className={styles[`${cssClassName}__text-input`]}
                />
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <label
                  className={styles[`${cssClassName}__field-label`]}
                  htmlFor="push-firmware-password"
                >
                  {texts.passwordLabel}
                </label>
                <div
                  className={styles[`${cssClassName}__password-input-wrapper`]}
                >
                  <input
                    id="push-firmware-password"
                    type={isPasswordVisible ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={texts.passwordPlaceholder}
                    className={styles[`${cssClassName}__password-input`]}
                  />
                  <IconButton
                    fun={handleTogglePasswordVisibility}
                    icon={isPasswordVisible ? eyeSlashIcon : eyeIcon}
                    tooltip={
                      isPasswordVisible
                        ? texts.hidePasswordTooltip
                        : texts.showPasswordTooltip
                    }
                    tooltipPosition="top"
                    variant="no-padding"
                  />
                </div>
              </FlexColumnContainer>

              <PrivateKeyUploadField
                label={texts.privateKeyFileForAuthLabel}
                fileName={privateKeyFile?.name ?? ""}
                onFileSelect={handlePrivateKeyFileSelection}
              />

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <label
                  className={styles[`${cssClassName}__field-label`]}
                  htmlFor="push-firmware-private-key-password"
                >
                  {texts.privateKeyPasswordLabel}
                </label>
                <input
                  id="push-firmware-private-key-password"
                  type="password"
                  value={privateKeyPassword}
                  onChange={handlePrivateKeyPasswordChange}
                  placeholder={texts.privateKeyPasswordPlaceholder}
                  className={styles[`${cssClassName}__text-input`]}
                />
              </FlexColumnContainer>

              <p className={styles[`${cssClassName}__helper-text`]}>
                {texts.helperText}
              </p>
            </FlexColumnContainer>
          </div>

          <div className={styles[`${cssClassName}__buttons-wrapper`]}>
            <OTASecondaryButton
              fun={handleCloseModal}
              text={texts.cancelButtonText}
              disabled={isAskForApprovalLoading}
            />
            <ButtonWithLoader
              fun={handleAskForApproval}
              text={texts.askForApprovalButtonText}
              loading={isAskForApprovalLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTAPushFirmwareModal;
